import React, { useRef } from "react";
import cn from "classnames";
import useErrors from "hooks/useErrors";
import useModal from "hooks/useModal";
import { noop } from "lib/utils";
import ErrorMessage from "components/ui/ErrorMessage";
import StationAutosuggest from "components/StationAutosuggest";
import { ReactComponent as CloseIcon } from "static/images/close.svg";

const ModalInput = ({
  className,
  clearErrors = noop,
  errors,
  inputClassName,
  name,
  placeholder,
  slideUp = false,
  value,
  'aria-label': ariaLabel,
  ...props
}) => {
  const { closeModal, showModal } = useModal();
  const skipFocus = useRef(false);

  // When the modal closes, it will automatically focus the input.
  const onModalClose = () => {
    skipFocus.current = true;
  }

  const onInputFocus = () => {
    if (skipFocus.current) {
      // Skip opening the modal the first time we focus after the modal closed
      // (to avoid be trapped in the modal forever)
      skipFocus.current = false;
    } else {
      // Otherwise we're going to open it
      handleModalTrigger();
    }
  }

  const handleModalTrigger = (event) => {
    let initialSearch;
    if (event?.type === "keyup" && event.key !== "ArrowDown") {
      // Show modal when user presses down arrow, otherwise ignore
      return;
    } else if (event?.type === 'change') {
      // Show modal when user types something, and initialise the search with what the user typed
      initialSearch = event.target.value;
    }

    showModal(
      <div className="otrl-jp__modal-input">
        <div className="otrl-jp__modal-input__header">
          <div className="otrl-jp__modal-input__header__bar">
            <h1 className="otrl-jp__modal-input__title">{placeholder}</h1>
            <button
              className="otrl-jp__modal-input__close"
              type="button"
              onClick={closeModal}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>
        </div>
        <StationAutosuggest
          autoFocus
          className={cn("otrl-jp__station-input", "otrl-jp__station-input--modal", className)}
          clearErrors={clearErrors}
          errors={errors}
          isModal
          name={name}
          onSelection={closeModal}
          placeholder={placeholder}
          value={value}
          initialSearch={initialSearch}
          {...props}
        />
      </div>,
      { autoFocus: false, onClose: onModalClose }
    );
  };

  const hasError = useErrors(errors, name);

  return (
    <>
      <input
        autocomplete="off"
        className={cn(inputClassName, { [`otrl-jp__station-input--error`]: hasError })}
        data-lpignore="true"
        onClick={handleModalTrigger}
        onKeyUp={handleModalTrigger}
        onChange={handleModalTrigger}
        onFocus={onInputFocus}
        name={name}
        placeholder={value ? undefined : placeholder}
        value={value?.name ?? ""}
        aria-label={ariaLabel}
        {...props}
      />
      <ErrorMessage errors={errors} name={name} />
    </>
  );
};

export default ModalInput;
